import { useForm, Controller } from "react-hook-form";
import { z } from "zod";

import { useUser } from "@components/user-context";
import { Badge, Checkbox, PrimaryButton, Select } from "@components/v2";
import { LoadingIndicator } from "@components/v2/mini-loading-indicator/mini-loading-indicator";
import { LearnMoreTooltip } from "@features/sales-v3/top-level-filters/components/learn-more-tooltip";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  ALLOWED_ATTRIBUTION_MODELS,
  DEFAULT_ATTRIBUTION_MODEL,
  useApexConsentForPartners,
} from "./use-apex-consent-for-partners";

// Define the zod schema
const apexSettingsFormSchema = z.object({
  revenueType: z.enum(["first_time", "returning", "blended"]),
  attributionModel: z.enum(ALLOWED_ATTRIBUTION_MODELS),
  attributionWindow: z.string(),
  accountingMode: z.enum(["accrual", "cash"]),
});

export function Apex() {
  const { user } = useUser();

  const {
    isLoadingApexConsent,
    apexConsentForPartners,
    getPartnerDisplayName,
    onChangeConsent,
    onSubmitUpdatedConsent,
    isUpdatingApex,
    availableAttributionModelOptions,
    availableAttributionWindowOptions,
    canToggleConsent,
    currentApexSettings: initialApexSettings,
  } = useApexConsentForPartners();

  const apexSettingsForm = useForm<z.infer<typeof apexSettingsFormSchema>>({
    resolver: zodResolver(apexSettingsFormSchema),
    defaultValues: {
      revenueType: "first_time",
      attributionModel: DEFAULT_ATTRIBUTION_MODEL,
      attributionWindow: availableAttributionWindowOptions["accrual"][0].value,
      accountingMode: "accrual",
    },
    values: initialApexSettings as z.infer<typeof apexSettingsFormSchema>,
  });

  const accountingMode = apexSettingsForm.watch("accountingMode");
  const attributionWindowOptions =
    availableAttributionWindowOptions[accountingMode];

  const onSubmit = apexSettingsForm.handleSubmit(async (values) =>
    onSubmitUpdatedConsent(values),
  );

  const divider = <hr className="my-4" />;

  const hasNoEnabledPartners = !apexConsentForPartners.length;

  return (
    <form className="container pt-4" onSubmit={onSubmit}>
      {!canToggleConsent && (
        <div
          className="flex gap-2 items-center alert alert-danger"
          role="alert"
        >
          <i className="fa-solid fa-triangle-exclamation text-v2-mars/600" />
          Only the account admin can update Apex settings!
        </div>
      )}
      <h1 className="flex gap-2 items-center mx-0 mt-0 mb-0 heading">
        Apex{" "}
        <Badge variant="alert" emphasis className="py-1 px-1.5 h-auto">
          New
        </Badge>
      </h1>
      <div className="mt-2 font-normal text-v2-primary">
        Accelerate performance with a push of a button. Northbeam Apex is a
        direct integration between Northbeam and ad platforms. <br />
        With Apex, ad platforms can integrate your trusted multi-touch
        performance signal into their algorithm's optimization process and
        optimize to your definition of success.
      </div>
      {divider}
      <h2 className="text-lg font-bold text-v2-primary">North Star Metric</h2>
      {isLoadingApexConsent && <LoadingIndicator />}
      {!isLoadingApexConsent && (
        <div className="inline-flex gap-2 items-center">
          <Controller
            name="revenueType"
            control={apexSettingsForm.control}
            render={({ field }) => (
              <Select
                key="north_star_revenue_type"
                buttonClassName="w-fit"
                dropdownClassName="w-[calc(var(--button-width)+10px)]"
                value={field.value}
                disabled={!canToggleConsent}
                options={[
                  { value: "first_time", label: "1st time revenue" },
                  { value: "returning", label: "Returning revenue" },
                  { value: "blended", label: "Blended revenue" },
                ]}
                onChange={(updatedRevenueType) =>
                  apexSettingsForm.setValue(
                    field.name,
                    updatedRevenueType ?? "first_time",
                  )
                }
              />
            )}
          />
          <span className="text-v2-primary">defined by</span>
          <Controller
            name="attributionModel"
            control={apexSettingsForm.control}
            render={({ field }) => (
              <Select
                key="north_star_attribution_model"
                buttonClassName="w-fit"
                dropdownClassName="w-[calc(var(--button-width)+80px)]"
                value={field.value}
                disabled={!canToggleConsent}
                options={availableAttributionModelOptions}
                onChange={(updatedAttributionModel) =>
                  apexSettingsForm.setValue(
                    field.name,
                    updatedAttributionModel ??
                      availableAttributionModelOptions[0].value,
                  )
                }
              />
            )}
          />
          <Controller
            name="attributionWindow"
            control={apexSettingsForm.control}
            render={({ field }) => (
              <Select
                key="north_star_attribution_window"
                buttonClassName="w-fit"
                dropdownClassName="w-[calc(var(--button-width)+20px)]"
                value={field.value}
                disabled={accountingMode === "cash" || !canToggleConsent}
                options={attributionWindowOptions}
                onChange={(updatedAttributionWindow) =>
                  apexSettingsForm.setValue(
                    field.name,
                    updatedAttributionWindow ??
                      attributionWindowOptions[0].value,
                  )
                }
              />
            )}
          />
          <Controller
            name="accountingMode"
            control={apexSettingsForm.control}
            render={({ field }) => (
              <Select
                key="north_star_accounting_mode"
                buttonClassName="w-fit"
                dropdownClassName="w-[calc(var(--button-width)+10px)]"
                value={field.value}
                disabled={!canToggleConsent}
                options={[
                  { value: "accrual", label: "Accrual" },
                  { value: "cash", label: "Cash" },
                ]}
                onChange={(updatedAccountingMode) => {
                  const nonNullUpdatedAccountingMode =
                    updatedAccountingMode ?? "accrual";

                  const updatedAttributionWindowOptions =
                    availableAttributionWindowOptions[
                      nonNullUpdatedAccountingMode
                    ];

                  const firstAttributionWindowOption =
                    updatedAttributionWindowOptions[0].value;

                  apexSettingsForm.setValue(
                    field.name,
                    nonNullUpdatedAccountingMode,
                  );
                  apexSettingsForm.setValue(
                    "attributionWindow",
                    firstAttributionWindowOption,
                  );
                }}
              />
            )}
          />
        </div>
      )}
      {divider}
      <h2 className="text-lg font-bold text-v2-primary">Apex Platforms</h2>
      <div className="mt-2 font-normal leading-5 text-v2-primary">
        Once enabled, the selected platforms will be able to apply optimizations
        every time your Northbeam MTA attribution is updated.
      </div>
      <div className="flex flex-col gap-2 items-start mt-2">
        {isLoadingApexConsent && <LoadingIndicator />}
        {!isLoadingApexConsent && hasNoEnabledPartners && (
          <i>
            <b>
              You don't have any partners enabled for Apex. Contact our{" "}
              <u>Sales team</u> to learn more about Apex and how to enable it.
            </b>
          </i>
        )}
        {apexConsentForPartners.map((partnerConsent) => {
          const partnerDisplayName = getPartnerDisplayName(
            partnerConsent.partner,
          );
          return (
            <LearnMoreTooltip
              key={partnerConsent.partner}
              title={`Missing connection for ${partnerDisplayName}`}
              content={`Before enabling Apex, you should connect an account for ${partnerDisplayName}.`}
              linkLabel="Connect account"
              link={user.pathFromRoot("/settings/connections")}
              noInfoCircle
              placement="top-start"
              showTooltip={
                canToggleConsent &&
                !partnerConsent.hasConsent &&
                !partnerConsent.hasConnectedAccounts
              }
            >
              <Checkbox
                label={partnerDisplayName}
                checked={partnerConsent.hasConsent}
                onChange={(event) =>
                  onChangeConsent(partnerConsent.partner, event.target.checked)
                }
                disabled={
                  !canToggleConsent ||
                  (!partnerConsent.hasConsent &&
                    !partnerConsent.hasConnectedAccounts)
                }
              />
            </LearnMoreTooltip>
          );
        })}
      </div>
      <PrimaryButton
        type="submit"
        className="mt-4"
        loading={isUpdatingApex}
        disabled={isUpdatingApex || hasNoEnabledPartners || !canToggleConsent}
      >
        Save
      </PrimaryButton>
    </form>
  );
}
